'use client'

import { formatTimestamp } from '@/lib/timed-ui/formatting'

interface WaveTimeSlot {
  id: string
  waveTime?: number
  startTimestamp?: number
  time?: number
  startTime?: number
  start?: number
  sold?: string
  availableQuantity?: number
  quantity?: number
}

interface TimeSlotGridProps {
  times: WaveTimeSlot[]
  selectedTime: WaveTimeSlot | null
  onSelect: (time: WaveTimeSlot) => void
}

const resolveTimeValue = (time: WaveTimeSlot): number | string | null => {
  return time.waveTime ?? time.startTimestamp ?? time.time ?? time.startTime ?? time.start ?? null
}

const formatTimeLabel = (rawValue: number | string | null | undefined, fallbackIndex: number): string => {
  if (rawValue === null || rawValue === undefined) {
    return `Session ${fallbackIndex + 1}`
  }

  if (typeof rawValue === 'number' && rawValue > 0) {
    return formatTimestamp(rawValue, 'h:mm a')
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim()
    if (!trimmed) return `Session ${fallbackIndex + 1}`

    if (/^\d{5,}$/.test(trimmed)) {
      return formatTimestamp(parseInt(trimmed), 'h:mm a')
    }

    const match = trimmed.match(/^(\d{1,2}):(\d{2})$/)
    if (match) {
      const hours = parseInt(match[1])
      const minutes = match[2]
      const period = hours >= 12 ? 'PM' : 'AM'
      const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours
      return `${displayHours}:${minutes} ${period}`
    }

    return trimmed
  }

  return `Session ${fallbackIndex + 1}`
}

const TimeSlotGrid = ({ times, selectedTime, onSelect }: TimeSlotGridProps) => {
  if (!times || times.length === 0) {
    return (
      <div className="text-center py-6 text-text-tertiary text-sm">
        No time slots available for this date
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-2.5">
      {times.map((time, index) => {
        const isSelected = selectedTime?.id === time.id
        const isSoldOut = time.sold === '1'
        const available = time.availableQuantity || 0
        const total = time.quantity || 0
        const percentage = total > 0 ? (available / total) * 100 : 100

        const isAlmostGone = !isSoldOut && total > 0 && percentage <= 10
        const isSellingFast = !isSoldOut && !isAlmostGone && total > 0 && percentage <= 20

        const rawValue = resolveTimeValue(time)
        const timeLabel = formatTimeLabel(rawValue, index)

        return (
          <button
            key={time.id}
            onClick={() => !isSoldOut && onSelect(time)}
            disabled={isSoldOut}
            className={`
              rounded-xl border px-4 py-3.5 text-center transition-all duration-200 animate-fade-in
              ${isSelected
                ? 'bg-primary text-text-inverse border-primary shadow-md ring-2 ring-primary/20'
                : isSoldOut
                  ? 'bg-surface-tertiary border-transparent text-text-tertiary cursor-not-allowed opacity-50'
                  : 'bg-surface border-surface-border text-text-primary hover:border-primary hover:shadow-sm cursor-pointer'
              }
            `}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <div className={`text-base font-bold ${isSoldOut ? 'line-through' : ''}`}>
              {timeLabel}
            </div>
            {isSoldOut ? (
              <div className="text-xs mt-1 text-text-tertiary">Sold Out</div>
            ) : isAlmostGone ? (
              <div className={`text-xs mt-1 font-semibold ${isSelected ? 'text-text-inverse/80' : 'text-error'}`}>Almost Gone</div>
            ) : isSellingFast ? (
              <div className={`text-xs mt-1 font-semibold ${isSelected ? 'text-text-inverse/80' : 'text-warning'}`}>Selling Fast</div>
            ) : available > 0 && total > 0 ? (
              <div className={`text-xs mt-1 ${isSelected ? 'text-text-inverse/70' : 'text-text-tertiary'}`}>
                {available} left
              </div>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export default TimeSlotGrid
